import {
    type ComponentType,
    type ReactElement,
    isValidElement,
    type ReactNode,
    Children,
    Fragment
} from "react";
import type {
    DropdownMenuSheetCollectConstantType,
    DropdownMenuSheetResolveConstantType,
    DropdownMenuSheetResolvedType,
    DropdownMenuSheetPageType
} from "./type";
import type IDropdownMenuCheckboxItemProps from "../checkboxItem/type";
import type IDropdownMenuGroupProps from "../group/type";
import type IDropdownMenuItemProps from "../item/type";
import type IDropdownMenuLabelProps from "../label/type";
import type IDropdownMenuRadioGroupProps from "../radioGroup/type";
import type IDropdownMenuRadioItemProps from "../radioItem/type";
import type IDropdownMenuSeparatorProps from "../separator/type";
import type IDropdownMenuSubProps from "../sub/type";
import type IDropdownMenuSubContentProps from "../subContent/type";
import type IDropdownMenuSubTriggerProps from "../subTrigger/type";
import DropdownMenuCheckboxItem from "../checkboxItem";
import PopoverSurface from "../../../popoverSurface";
import DropdownMenuRadioGroup from "../radioGroup";
import DropdownMenuSubContent from "../subContent";
import DropdownMenuSubTrigger from "../subTrigger";
import DropdownMenuRadioItem from "../radioItem";
import DropdownMenuSeparator from "../separator";
import DropdownMenuStatus from "../status";
import DropdownMenuEmpty from "../empty";
import DropdownMenuGroup from "../group";
import DropdownMenuLabel from "../label";
import DropdownMenuItem from "../item";
import DropdownMenuSub from "../sub";

const isElementOf = <P>(child: ReactNode, type: ComponentType<P>): child is ReactElement<P> => {
    return isValidElement(child) && child.type === type;
};

const isFragmentElement = (child: ReactElement): child is ReactElement<{
    children?: ReactNode;
}> => child.type === Fragment;

const getSubParts = (children: ReactNode) => {
    const parts = Children.toArray(children);

    return {
        content: parts.find((part): part is ReactElement<IDropdownMenuSubContentProps> => isElementOf(part, DropdownMenuSubContent)),
        trigger: parts.find((part): part is ReactElement<IDropdownMenuSubTriggerProps> => isElementOf(part, DropdownMenuSubTrigger))
    };
};

export const collectDropdownMenuSheetPage = ({
    keyPrefix,
    children,
    radio
}: DropdownMenuSheetCollectConstantType): DropdownMenuSheetPageType => {
    const page: DropdownMenuSheetPageType = {
        records: [],
        extras: []
    };

    const append = (nestedPage: DropdownMenuSheetPageType) => {
        page.records.push(...nestedPage.records);

        page.extras.push(...nestedPage.extras);
    };

    Children.toArray(children).forEach((child, index) => {
        const key = `${keyPrefix}${index}`;

        if(!isValidElement(child)) {
            return;
        }

        if(isFragmentElement(child)) {
            append(collectDropdownMenuSheetPage({
                children: child.props.children,
                keyPrefix: `${key}-`,
                radio
            }));

            return;
        }

        if(isElementOf<IDropdownMenuItemProps>(child, DropdownMenuItem)) {
            const {
                isCloseOnSelect,
                disabledReason,
                isDisabled,
                subTitle,
                onPress,
                title,
                type,
                icon
            } = child.props;

            page.records.push({
                item: {
                    description: isDisabled && disabledReason ? disabledReason : subTitle,
                    label: title,
                    isDisabled,
                    type,
                    icon
                },
                isCloseOnPress: isCloseOnSelect ?? true,
                onPress,
                key
            });

            return;
        }

        if(isElementOf<IDropdownMenuCheckboxItemProps>(child, DropdownMenuCheckboxItem)) {
            const {
                isCloseOnSelect,
                disabledReason,
                isDisabled,
                isChecked,
                onChange,
                subTitle,
                title
            } = child.props;

            page.records.push({
                item: {
                    description: isDisabled && disabledReason ? disabledReason : subTitle,
                    "aria-checked": isChecked === "partially" ? "mixed" : !!isChecked,
                    role: "menuitemcheckbox",
                    label: title,
                    isDisabled
                },
                isCloseOnPress: isCloseOnSelect ?? false,
                onPress: () => {
                    onChange?.(isChecked !== true);
                },
                isChecked: isChecked === true,
                trailing: "switch",
                key
            });

            return;
        }

        if(isElementOf<IDropdownMenuRadioItemProps>(child, DropdownMenuRadioItem)) {
            const {
                isCloseOnSelect,
                disabledReason,
                isDisabled,
                subTitle,
                onPress,
                title,
                value
            } = child.props;

            const isChecked = radio?.value === value;

            page.records.push({
                item: {
                    description: isDisabled && disabledReason ? disabledReason : subTitle,
                    "aria-checked": isChecked,
                    isSelected: isChecked,
                    role: "menuitemradio",
                    isShowCheck: true,
                    label: title,
                    isDisabled
                },
                isCloseOnPress: isCloseOnSelect ?? true,
                onPress: () => {
                    radio?.onChange?.(value);

                    onPress?.();
                },
                key
            });

            return;
        }

        if(isElementOf<IDropdownMenuLabelProps>(child, DropdownMenuLabel)) {
            page.records.push({
                heading: child.props.title,
                key
            });

            return;
        }

        if(isElementOf<IDropdownMenuGroupProps>(child, DropdownMenuGroup)) {
            if(child.props.title) {
                page.records.push({
                    heading: child.props.title,
                    key: `${key}-heading`
                });
            }

            append(collectDropdownMenuSheetPage({
                children: child.props.children,
                keyPrefix: `${key}-`,
                radio
            }));

            return;
        }

        if(isElementOf<IDropdownMenuRadioGroupProps>(child, DropdownMenuRadioGroup)) {
            if(child.props.title) {
                page.records.push({
                    heading: child.props.title,
                    key: `${key}-heading`
                });
            }

            append(collectDropdownMenuSheetPage({
                radio: {
                    onChange: child.props.onChange,
                    value: child.props.value
                },
                children: child.props.children,
                keyPrefix: `${key}-`
            }));

            return;
        }

        if(isElementOf<IDropdownMenuSubProps>(child, DropdownMenuSub)) {
            const {
                content,
                trigger
            } = getSubParts(child.props.children);

            if(!trigger) {
                return;
            }

            const {
                disabledReason,
                isDisabled,
                valueText,
                subTitle,
                title,
                icon
            } = trigger.props;

            page.records.push({
                item: {
                    description: isDisabled && disabledReason ? disabledReason : subTitle,
                    "aria-haspopup": "menu",
                    meta: valueText,
                    label: title,
                    isDisabled,
                    icon
                },
                subChildren: content?.props.children,
                isCloseOnPress: false,
                trailing: "chevron",
                subTitle: title,
                key
            });

            return;
        }

        if(isElementOf<IDropdownMenuSeparatorProps>(child, DropdownMenuSeparator)) {
            return;
        }

        if(isElementOf(child, DropdownMenuStatus) || isElementOf(child, DropdownMenuEmpty) || child.type === PopoverSurface.Status || child.type === PopoverSurface.Empty) {
            page.extras.push(child);
        }
    });

    return page;
};

export const resolveDropdownMenuSheetPage = ({
    children,
    title,
    path
}: DropdownMenuSheetResolveConstantType): DropdownMenuSheetResolvedType => {
    let page = collectDropdownMenuSheetPage({
        keyPrefix: "",
        children
    });

    let currentTitle = title;

    let backTitle: string | undefined;

    for(const key of path) {
        const record = page.records.find((item) => item.key === key);

        if(!record?.subChildren) {
            break;
        }

        backTitle = currentTitle;

        currentTitle = record.subTitle;

        page = collectDropdownMenuSheetPage({
            children: record.subChildren,
            keyPrefix: `${key}.`
        });
    }

    const isReserveLeading = page.records.some((record) => !!record.item?.icon);

    return {
        ...page,
        records: isReserveLeading ? page.records.map((record) => {
            if(record.heading !== undefined || !record.item) {
                return {
                    ...record,
                    isReserveLeading: true
                };
            }

            return {
                ...record,
                item: {
                    ...record.item,
                    isReserveLeading: !record.item.icon
                }
            };
        }) : page.records,
        title: currentTitle,
        backTitle
    };
};
